import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { verifyPassword } from '../utils/passwordUtils';

const ADMIN_EMAIL = 'admin@dsh.com'; // Use your actual admin email

function Login() {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('Login component mounted');
    
    // Clear any existing session data when login page loads
    // This prevents old session data from interfering with new login attempts
    localStorage.removeItem('customUser');
    localStorage.removeItem('userRole');
  }, []);

  // Debug: Log form state changes
  useEffect(() => {
    console.log('Form state changed:', { emailOrPhone, password: password ? '***' : '', error, loading });
  }, [emailOrPhone, password, error, loading]);

  // Clear errors when user starts typing
  useEffect(() => {
    if (emailOrPhone) setEmailError('');
    if (password) setPasswordError('');
  }, [emailOrPhone, password]);

  const validateForm = () => {
    let isValid = true;
    
    // Clear previous errors
    setEmailError('');
    setPasswordError('');
    
    // Validate email/phone field
    if (!emailOrPhone.trim()) {
      setEmailError('Email or phone number is required');
      isValid = false;
    }
    
    // Validate password field
    if (!password.trim()) {
      setPasswordError('Password is required');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Custom validation
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    // Clear any existing session data before new login attempt
    localStorage.removeItem('customUser');
    localStorage.removeItem('userRole');
    
    console.log('Login attempt:', { emailOrPhone, password: '***' });
    
    try {
      // Handle phone number login
      if (/^03\d{9}$/.test(emailOrPhone)) {
        // First try custom authentication (for new users)
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, where('contactNumber', '==', emailOrPhone));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const userData = userDoc.data();
          console.log('Found user data:', userData);
          
          // Check if password matches using bcrypt verification
          console.log('Password verification for user:', userData.uid);
          const isPasswordValid = await verifyPassword(password, userData.password);
          console.log('Password verification result:', isPasswordValid);
          
          if (isPasswordValid) {
            // Check if user is disabled
            if (userData.disabled) {
              setError('Your account has been disabled. Please contact the administrator.');
              setLoading(false);
              return;
            }
            
            // Create a custom auth session
            const customUser = {
              uid: userData.uid,
              email: userData.authEmail,
              displayName: userData.fullName
            };
            
            console.log('Custom auth successful, user data:', userData);
            console.log('User role from Firestore:', userData.role);
            console.log('Storing role in localStorage:', userData.role);
            
            // Store user info in localStorage for session management
            localStorage.setItem('customUser', JSON.stringify(customUser));
            localStorage.setItem('userRole', userData.role);
            
            // Navigate based on role
            console.log('Login successful, navigating to:', userData.role);
            const targetRoute = userData.role === 'admin' || userData.role === 'manager' 
              ? '/admin' 
              : userData.role === 'employee' || userData.role === 'internee'
                ? '/employee-dashboard' 
                : userData.role === 'student' 
                  ? '/student-dashboard' 
                  : '/login';
            
            console.log('Target route:', targetRoute);
            console.log('About to navigate to:', targetRoute);
            navigate(targetRoute);
            console.log('Navigation called');
            setLoading(false);
            return;
          } else {
            console.log('Password mismatch - stored:', userData.password, 'entered:', password);
            setError('Invalid password');
            setLoading(false);
            return;
          }
        }
        
        // If not found in custom auth, try Firebase Auth
        try {
          const userCredential = await signInWithEmailAndPassword(auth, `${emailOrPhone}@company.com`, password);
          const userRef = doc(firestore, 'users', userCredential.user.uid);
          const snap = await getDoc(userRef);
          
          if (snap.exists()) {
            const userData = snap.data();
            
            // Check if user is disabled
            if (userData.disabled) {
              setError('Your account has been disabled. Please contact the administrator.');
              setLoading(false);
              return;
            }
            
            const role = userData.role;
            const targetRoute = role === 'admin' || role === 'manager' 
              ? '/admin' 
              : role === 'employee' || role === 'internee'
                ? '/employee-dashboard' 
                : '/login';
            
            console.log('Phone login (company) - Target route:', targetRoute);
            navigate(targetRoute);
            setLoading(false);
            return;
          } else {
            setError('User data not found.');
            setLoading(false);
            return;
          }
        } catch (err) {
          // Try student login
          try {
            const userCredential = await signInWithEmailAndPassword(auth, `${emailOrPhone}@student.com`, password);
            const userRef = doc(firestore, 'users', userCredential.user.uid);
            const snap = await getDoc(userRef);
            
            if (snap.exists()) {
              const userData = snap.data();
              
              // Check if user is disabled
              if (userData.disabled) {
                setError('Your account has been disabled. Please contact the administrator.');
                setLoading(false);
                return;
              }
              
              const role = userData.role;
              const targetRoute = role === 'student' ? '/student-dashboard' : '/login';
              
              console.log('Phone login (student) - Target route:', targetRoute);
              navigate(targetRoute);
              setLoading(false);
              return;
            } else {
              setError('User data not found.');
              setLoading(false);
              return;
            }
          } catch (err2) {
            setError('Invalid phone number or password');
            setLoading(false);
            return;
          }
        }
      } else {
        // Handle email login (admin/manager)
        try {
          console.log('Attempting Firebase Auth login with:', emailOrPhone);
          const userCredential = await signInWithEmailAndPassword(auth, emailOrPhone, password);
          console.log('Firebase Auth successful, UID:', userCredential.user.uid);
          
          const userRef = doc(firestore, 'users', userCredential.user.uid);
          const snap = await getDoc(userRef);
          
          if (snap.exists()) {
            const userData = snap.data();
            console.log('User document found:', userData);
            
            // Check if user is disabled
            if (userData.disabled) {
              setError('Your account has been disabled. Please contact the administrator.');
              setLoading(false);
              return;
            }
            
            const role = userData.role;
            console.log('Email login - User role:', role);
            console.log('User data for Firebase Auth:', userData);
            
            // Clear any custom session data since we're using Firebase Auth
            localStorage.removeItem('customUser');
            localStorage.removeItem('userRole');
            
            const targetRoute = role === 'admin' || role === 'manager' 
              ? '/admin' 
              : role === 'employee' || role === 'internee'
                ? '/employee-dashboard' 
                : role === 'student' 
                  ? '/student-dashboard' 
                  : '/login';
            
            console.log('Firebase Auth - Target route:', targetRoute);
            navigate(targetRoute);
          } else {
            console.log('No user document found for UID:', userCredential.user.uid);
            setError('User data not found.');
          }
          setLoading(false);
          return;
        } catch (err) {
          console.log('Firebase Auth error:', err.message);
          setError('Invalid email or password');
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20"></div>

      {/* Floating Particles for extra effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/40 rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-white/60 rounded-full animate-ping"></div>
        <div className="absolute bottom-1/3 left-1/3 w-3 h-3 bg-white/30 rounded-full animate-bounce"></div>
        <div className="absolute top-2/3 right-1/3 w-1.5 h-1.5 bg-white/50 rounded-full animate-pulse"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Login Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 p-8">
            {/* Logo Section - Moved inside card at top */}
            <div className="text-center mb-6">
              <img 
                src="/DSH.png" 
                alt="DSH Logo" 
                className="mx-auto w-24 h-24 object-contain drop-shadow-lg"
              />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3 text-center">Welcome Back !</h2>
            <h4 className="text-xl text-white mb-6 text-center">Student Login✍️</h4>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email/Phone Input with Floating Label */}
              <div className="relative">
                <input
                  type="text"
                  placeholder=""
                  value={emailOrPhone}
                  onChange={e => setEmailOrPhone(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  className={`w-full px-4 py-3 bg-white/20 backdrop-blur-sm border rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 ${
                    emailError ? 'border-red-400/50' : 'border-white/30'
                  }`}
                />
                <label 
                  className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    emailFocused || emailOrPhone 
                      ? '-top-2 text-xs text-white/80 bg-black/20 px-2 py-1 rounded' 
                      : 'top-1/2 transform -translate-y-1/2 text-white/60'
                  }`}
                >
                  Enter your email or phone
                </label>
                {emailError && (
                  <div className="mt-1 text-red-300 text-xs flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {emailError}
                  </div>
                )}
              </div>
              
              {/* Password Input with Floating Label */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder=""
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className={`w-full px-4 py-3 pr-12 bg-white/20 backdrop-blur-sm border rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 ${
                    passwordError ? 'border-red-400/50' : 'border-white/30'
                  }`}
                />
                <label 
                  className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    passwordFocused || password 
                      ? '-top-2 text-xs text-white/80 bg-black/20 px-2 py-1 rounded' 
                      : 'top-1/2 transform -translate-y-1/2 text-white/60'
                  }`}
                >
                  Enter your password
                </label>
                {/* Eye Icon for Password Toggle */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors duration-200 focus:outline-none"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                {passwordError && (
                  <div className="mt-1 text-red-300 text-xs flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {passwordError}
                  </div>
                )}
              </div>
              
              {error && (
                <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/30 text-red-200 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
              
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            
            {/* Additional Info */}
            <div className="mt-6 text-center">
              <p className="text-white/70 text-sm">
                Secure access to your educational portal
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;


